import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';

/**
 * Smart QR Hook: parses whatever the scanner decoded, extracts the doctor
 * slug (`docsite.care/dr/<slug>` or bare `<slug>`), silently saves the clinic
 * to the patient's "My Clinics" list, then redirects to the booking page.
 *
 * Returns: { run, saving, error }
 */
export default function useSmartQRScan({ onSaved } = {}) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const extractSlug = useCallback((raw) => {
    if (!raw) return null;
    const trimmed = String(raw).trim();
    // Accept full URL like https://docsite.care/dr/<slug>?ref=...
    const m = trimmed.match(/(?:^|\/)dr\/([a-z0-9-]+)(?:$|[/?#])/i);
    if (m) return m[1];
    // Accept a bare slug typed by the user
    if (/^[a-z0-9][a-z0-9-]{1,80}$/i.test(trimmed)) return trimmed;
    return null;
  }, []);

  const run = useCallback(async (qrData) => {
    setError('');
    const slug = extractSlug(qrData);
    if (!slug) {
      setError('That doesn’t look like a DocSite doctor link. Please try again.');
      return null;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/patient/save-clinic', { doctor_slug: slug });
      onSaved?.(data.clinic);
      navigate(`/dr/${slug}`);
      return data.clinic;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        // Not logged in — bounce to register with zero-touch seed
        navigate(`/register?connect_to=${encodeURIComponent(slug)}`);
        return null;
      }
      setError(typeof detail === 'string' ? detail : 'Could not save clinic. Please try again.');
      return null;
    } finally {
      setSaving(false);
    }
  }, [extractSlug, navigate, onSaved]);

  return { run, saving, error, setError };
}
